import React, { useState } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  MenuItem,
  Snackbar,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  useMediaQuery
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import {
  Person,
  Email,
  Phone,
  Home,
  Work,
  Business,
  DirectionsCar,
  Label,
  Description,
  AttachMoney,
  CalendarMonth,
  CloudUpload,
  CheckCircle
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ResponsiveAppBar from "../components/AppBar";
import Footer from "../components/Footer";
import { Wallet } from "lucide-react";
import { API_BASE_URL } from "../api/config";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  background: "linear-gradient(145deg, #f7f9fc 0%, #eef1f5 100%)",
  transition: "transform 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-5px)"
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  "& .MuiOutlinedInput-root": {
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 2px 5px rgba(0,0,0,0.1)"
    },
    "&.Mui-focused": {
      boxShadow: "0 0 10px rgba(25,118,210,0.3)"
    }
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 3),
  fontWeight: "bold", 
  borderRadius: theme.spacing(1),
  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 12px rgba(0,0,0,0.15)"
  }
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ImagePreview = styled('img')({
  maxWidth: '100%',
  maxHeight: '200px',
  objectFit: 'contain',
  margin: '10px 0',
  borderRadius: '8px'
});

const BASE_URL = API_BASE_URL;

const VehicleLoanForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    address: "",
    occupation: "",
    employerName: "",
    monthlyIncome: "",
    vehicleName: "",
    vehicleModel: "",
    loanAmount: "",
    loanTerm: "",
    vehicleType: "",
    employmentStatus: "",
    vehicleImage: null,
  });

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const steps = ['Personal Details', 'Vehicle Information', 'Review & Submit'];

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "vehicleImage" && files && files[0]) {
      setFormData({ ...formData, vehicleImage: files[0] });
      setImagePreview(URL.createObjectURL(files[0]));
    } else {
      setFormData({ ...formData, [name]: value });
    }
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Create FormData for submission
    const formDataToSubmit = new FormData();
    for (const key in formData) {
      if (formData.hasOwnProperty(key)) {
        formDataToSubmit.append(key, formData[key]);
      }
    }

    try {
      // Simulate API call (uncomment and replace with actual API call)
      const response = await axios.post(
        `${BASE_URL}/vehicle-loan/add_vehicleloan`,
        formDataToSubmit,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setSuccessMessage("Your vehicle loan application has been submitted successfully!");
      setOpenSnackbar(true);
      setIsSubmitting(false);
      setShowSuccess(true);

      // Reset form
      setFormData({
        fullName: "",
        email: "",
        phoneNumber: "",
        address: "",
        occupation: "",
        employerName: "",
        monthlyIncome: "",
        vehicleName: "",
        vehicleModel: "",
        loanAmount: "",
        loanTerm: "",
        vehicleType: "",
        employmentStatus: "",
        vehicleImage: null,
      });
      setImagePreview(null);
    } catch (error) {
      setIsSubmitting(false);
      if (error.response && error.response.status === 409) {
        setError(error.response.data.message);
      } else {
        setError("Something went wrong. Please try again later.");
      }
    }
  };

  const handleNext = () => {
    // Validate current step
    if (activeStep === 0) {
      if (!formData.fullName || !formData.phoneNumber || 
          !formData.address || !formData.employmentStatus) {
        setError("Please fill in all required fields");
        return;
      }
      
      if (formData.employmentStatus === "Employed" && 
          (!formData.occupation || !formData.employerName || !formData.monthlyIncome)) {
        setError("Please fill in all employment details");
        return;
      }
    } else if (activeStep === 1) {
      if (!formData.vehicleType || !formData.vehicleName || 
          !formData.vehicleModel || !formData.loanAmount || !formData.loanTerm) {
        setError("Please fill in all vehicle and loan details");
        return;
      }
    }
    
    setError("");
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError("");
  };

  const handleReset = () => {
    setActiveStep(0);
    setShowSuccess(false);
    setFormData({
      fullName: "",
      email: "",
      phoneNumber: "",
      address: "",
      occupation: "",
      employerName: "",
      monthlyIncome: "",
      vehicleName: "",
      vehicleModel: "",
      loanAmount: "",
      loanTerm: "",
      vehicleType: "",
      employmentStatus: "",
      vehicleImage: null,
    });
    setImagePreview(null);
  };

  // Calculate estimated monthly payment
  const calculateMonthlyPayment = () => {
    const principal = parseFloat(formData.loanAmount);
    const interestRate = 0.085 / 12; // 8.5% annual interest rate
    const numberOfPayments = parseFloat(formData.loanTerm);
    
    if (!principal || !numberOfPayments) return "0";
    
    const monthlyPayment = 
      (principal * interestRate * Math.pow(1 + interestRate, numberOfPayments)) / 
      (Math.pow(1 + interestRate, numberOfPayments) - 1);
    
    return monthlyPayment.toLocaleString('en-IN', {
      maximumFractionDigits: 0,
      style: 'currency',
      currency: 'INR'
    });
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2, animation: 'fadeIn 0.5s ease-in-out' }}>
            <StyledTextField
              label="Full Name"
              name="fullName"
              fullWidth
              value={formData.fullName}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                ),
              }}
            />
            
            <StyledTextField
              label="Email"
              name="email"
              fullWidth
              value={formData.email}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />
            
            <StyledTextField
              label="Phone Number"
              type="tel"
              name="phoneNumber"
              fullWidth
              value={formData.phoneNumber}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone />
                  </InputAdornment>
                ),
              }}
            />
            
            <StyledTextField
              label="Address"
              name="address"
              fullWidth
              multiline
              rows={2}
              value={formData.address}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Home />
                  </InputAdornment>
                ),
              }}
            />
            
            <StyledTextField
              select
              label="Employment Status"
              name="employmentStatus"
              value={formData.employmentStatus}
              onChange={handleChange}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Work />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">Select Status</MenuItem>
              <MenuItem value="Employed">Employed</MenuItem>
              <MenuItem value="Unemployed">Unemployed</MenuItem>
            </StyledTextField>
            
            {(formData.employmentStatus === "Employed" || formData.employmentStatus === "Self-Employed") && (
              <>
                <StyledTextField
                  label="Occupation"
                  name="occupation"
                  fullWidth
                  value={formData.occupation}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Work />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <StyledTextField
                  label="Employer Name"
                  name="employerName"
                  fullWidth
                  value={formData.employerName}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Business />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <StyledTextField
                  label="Monthly Income"
                  type="number"
                  name="monthlyIncome"
                  fullWidth
                  value={formData.monthlyIncome}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoney />
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
          </Box>
        );
        
      case 1:
        return (
          <Box sx={{ mt: 2, animation: 'fadeIn 0.5s ease-in-out' }}>
            <StyledTextField
              select
              label="Vehicle Type"
              name="vehicleType"
              value={formData.vehicleType}
              onChange={handleChange}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DirectionsCar />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">Select Vehicle Type</MenuItem>
              <MenuItem value="Car">Car</MenuItem>
              <MenuItem value="Bike">Bike</MenuItem>
            </StyledTextField>
            
            <StyledTextField
              label="Vehicle Name"
              name="vehicleName"
              fullWidth
              value={formData.vehicleName}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Label />
                  </InputAdornment>
                ),
              }}
            />
            
            <StyledTextField
              label="Vehicle Model"
              name="vehicleModel"
              fullWidth
              value={formData.vehicleModel}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Description />
                  </InputAdornment>
                ),
              }}
            />
            
            <StyledTextField
              label="Loan Amount (Rs.)"
              type="number"
              name="loanAmount"
              fullWidth
              value={formData.loanAmount}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Wallet />
                  </InputAdornment>
                ),
              }}
            />
            
            <StyledTextField
              select
              label="Loan Term (months)"
              name="loanTerm"
              fullWidth
              value={formData.loanTerm}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonth />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">Select Loan Term</MenuItem>
              <MenuItem value="12">12 months</MenuItem>
              <MenuItem value="24">24 months</MenuItem>
              <MenuItem value="36">36 months</MenuItem>
              <MenuItem value="48">48 months</MenuItem>
              <MenuItem value="60">60 months</MenuItem>
              <MenuItem value="72">72 months</MenuItem>
              <MenuItem value="84">84 months</MenuItem>
            </StyledTextField>
            
            {formData.loanAmount && formData.loanTerm && (
              <Card sx={{ 
                mt: 2, 
                mb: 2, 
                bgcolor: '#e3f2fd', 
                boxShadow: '0 4px 12px rgba(25,118,210,0.15)' 
              }}>
                <CardContent>
                  <Typography variant="subtitle2" color="primary">
                    Estimated Monthly Payment
                  </Typography>
                  <Typography variant="h4" color="primary.dark" fontWeight="bold">
                    {calculateMonthlyPayment()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Based on 8.5% annual interest rate
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        );
        
      case 2:
        return (
          <Box sx={{ mt: 2, animation: 'fadeIn 0.5s ease-in-out' }}>
            <Card sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Upload Vehicle Image
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                p: 3,
                border: '2px dashed #ccc',
                borderRadius: 2,
                bgcolor: '#fafafa',
                mb: 2
              }}>
                {imagePreview ? (
                  <ImagePreview src={imagePreview} alt="Vehicle Preview" />
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center' 
                  }}>
                    <CloudUpload sx={{ fontSize: 60, color: '#bdbdbd', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Upload vehicle image (JPG, PNG, or GIF)
                    </Typography>
                  </Box>
                )}
                
                <Button
                  component="label"
                  variant="contained"
                  color="primary"
                  startIcon={<CloudUpload />}
                  sx={{ mt: 2 }}
                >
                  {imagePreview ? "Change Image" : "Upload Image"}
                  <VisuallyHiddenInput 
                    type="file" 
                    name="vehicleImage"
                    onChange={handleChange}
                    accept="image/*"
                    required
                  />
                </Button>
              </Box>
            </Card>
            
            <Card sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Application Summary</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
                    <Typography variant="body1" gutterBottom fontWeight="medium">{formData.fullName}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                    <Typography variant="body1" gutterBottom fontWeight="medium">{formData.email}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                    <Typography variant="body1" gutterBottom fontWeight="medium">{formData.phoneNumber}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Employment</Typography>
                    <Typography variant="body1" gutterBottom fontWeight="medium">{formData.employmentStatus}</Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1.5 }} />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Vehicle Type</Typography>
                    <Typography variant="body1" gutterBottom fontWeight="medium">{formData.vehicleType}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Vehicle</Typography>
                    <Typography variant="body1" gutterBottom fontWeight="medium">
                      {formData.vehicleName} {formData.vehicleModel}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Loan Amount</Typography>
                    <Typography variant="body1" gutterBottom fontWeight="medium">
                      Rs.{formData.loanAmount}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Loan Term</Typography>
                    <Typography variant="body1" gutterBottom fontWeight="medium">
                      {formData.loanTerm} months
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Monthly Payment</Typography>
                    <Typography variant="body1" gutterBottom fontWeight="medium" color="primary.dark">
                      {calculateMonthlyPayment()}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        );
        
      default:
        return 'Unknown step';
    }
  };

  // CSS for animations
  const animationStyles = `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    @keyframes pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }
  `;

  return (
    <>
    <ResponsiveAppBar/>
      <style>{animationStyles}</style>
      
      {showSuccess ? (
        <Container maxWidth="sm" sx={{ mt: 8, mb: 8 }}>
          <StyledPaper elevation={3} sx={{ 
            textAlign: 'center',
            py: 5,
            animation: 'pulse 2s ease-in-out infinite'
          }}>
            <CheckCircle color="success" sx={{ fontSize: 80, mb: 3 }} />
            
            <Typography variant="h4" gutterBottom color="primary">
              Application Submitted!
            </Typography>
            
            <Typography variant="body1" paragraph color="text.secondary">
              Your vehicle loan application has been submitted successfully. 
              Our team will review your application and contact you within 2-3 business days.
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 4 }} color="text.secondary">
              Reference ID: VL-{Math.floor(100000 + Math.random() * 900000)}
            </Typography>
            
            <StyledButton 
              variant="contained" 
              color="primary"
              onClick={handleReset}
            >
              Apply for Another Loan
            </StyledButton>
          </StyledPaper>
        </Container>
      ) : (
        <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
          <StyledPaper elevation={3}>
            <Typography variant="h4" align="center" gutterBottom fontWeight="bold" color="primary.dark" sx={{ mb: 4 }}>
              Vehicle Loan Application
            </Typography>
            
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{!isMobile ? label : ''}</StepLabel>
                </Step>
              ))}
            </Stepper>
            
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              {getStepContent(activeStep)}
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  variant="outlined"
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>
                
                <Box sx={{ position: 'relative' }}>
                  {activeStep === steps.length - 1 ? (
                    <StyledButton
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Application'}
                      {isSubmitting && (
                        <CircularProgress
                          size={24}
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginTop: '-12px',
                            marginLeft: '-12px',
                          }}
                        />
                      )}
                    </StyledButton>
                  ) : (
                    <StyledButton
                      variant="contained"
                      color="primary"
                      onClick={handleNext}
                    >
                      Next
                    </StyledButton>
                  )}
                </Box>
              </Box>
            </Box>
          </StyledPaper>
        </Container>
      )}
      
      <Snackbar 
        open={openSnackbar} 
        autoHideDuration={6000} 
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
      <Footer />
    </>
  );
};

export default VehicleLoanForm;